/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/FunctionsTab.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                         |
 * |---|------------------------|-------------------|------------------|--------------------------------------|
 * | 1 | 2026-06-21             | Claude Sonnet 4.6 | Function Library | Created — Function Library UI         |
 * | 2 | 2026-06-21             | Claude Sonnet 4.6 | FR1              | Added folder/subfolder hierarchy      |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<FunctionsTab />)
 *
 * =============================================================================
 */

import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { generateId } from "@mimiq/ir";
import type { IRStep } from "@mimiq/ir";
import {
  db,
  saveSharedFlow, deleteSharedFlow, duplicateSharedFlow,
  saveSharedFlowFolder, deleteSharedFlowFolder,
} from "../db/index.js";
import type { SharedFlow, SharedFlowFolder } from "../db/schema.js";
import {
  downloadSharedFlow, downloadSharedFlowsAsZip, importSharedFlows,
} from "../utils/exportUtils.js";
import { stepDescription, StepEditForm, NESTED_STEP_TYPES, makeDefaultNestedStep } from "../App.js";

// ── Props ─────────────────────────────────────────────────────────────────────

interface FunctionsTabProps {
  onEditFunction: (fn: SharedFlow) => void;
}

// ── Root component ────────────────────────────────────────────────────────────

export function FunctionsTab({ onEditFunction }: FunctionsTabProps) {
  const functions = useLiveQuery(() => db.sharedFlows.orderBy("name").toArray(), []) ?? [];
  const folders   = useLiveQuery(() => db.sharedFlowFolders.orderBy("name").toArray(), []) ?? [];

  const [selectedId, setSelectedId]     = useState<string | null>(null);
  const [exporting, setExporting]       = useState(false);
  const [importing, setImporting]       = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // Auto-expand all folders when they load or a new one is created
  const prevFolderIds = useState(() => new Set<string>())[0];
  if (folders.some(f => !prevFolderIds.has(f.id))) {
    folders.forEach(f => { prevFolderIds.add(f.id); expandedFolders.add(f.id); });
  }
  const [search, setSearch]             = useState("");
  const [toast, setToast]               = useState<string | null>(null);

  // Create function dialog
  const [creating, setCreating]         = useState(false);
  const [newName, setNewName]           = useState("");
  const [newDesc, setNewDesc]           = useState("");
  const [newFolderId, setNewFolderId]   = useState<string | undefined>(undefined);

  // Create folder dialog
  const [creatingFolder, setCreatingFolder]       = useState(false);
  const [newFolderName, setNewFolderName]         = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState<string | undefined>(undefined);

  // Rename / inline edit
  const [renaming, setRenaming]     = useState<{ kind: "fn" | "folder"; id: string } | null>(null);
  const [renameVal, setRenameVal]   = useState("");

  const selectedFn = functions.find(f => f.id === selectedId) ?? null;

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function toggleFolder(id: string) {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // ── Create function ──────────────────────────────────────────────────────────

  async function handleCreate() {
    if (!newName.trim()) return;
    const fn = await saveSharedFlow({ name: newName.trim(), description: newDesc.trim() || undefined, folderId: newFolderId });
    setCreating(false); setNewName(""); setNewDesc(""); setNewFolderId(undefined);
    setSelectedId(fn.id);
    showToast(`Function "${fn.name}" created`);
  }

  // ── Create folder ────────────────────────────────────────────────────────────

  async function handleCreateFolder() {
    if (!newFolderName.trim()) return;
    const folder = await saveSharedFlowFolder({ name: newFolderName.trim(), parentId: newFolderParentId });
    setCreatingFolder(false); setNewFolderName(""); setNewFolderParentId(undefined);
    setExpandedFolders(prev => new Set([...prev, folder.id]));
    showToast(`Folder "${folder.name}" created`);
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  async function handleDeleteFn(fn: SharedFlow) {
    if (!confirm(`Delete function "${fn.name}"? Any tests that call it will error until fixed.`)) return;
    await deleteSharedFlow(fn.id);
    if (selectedId === fn.id) setSelectedId(null);
    showToast(`Deleted "${fn.name}"`);
  }

  async function handleDeleteFolder(folder: SharedFlowFolder) {
    const childFunctions = functions.filter(f => f.folderId === folder.id).length;
    const childFolders   = folders.filter(f => f.parentId === folder.id).length;
    const warn = childFunctions + childFolders > 0
      ? `\n\nWarning: ${childFunctions} function${childFunctions !== 1 ? "s" : ""} and ${childFolders} subfolder${childFolders !== 1 ? "s" : ""} will be moved to the root level.`
      : "";
    if (!confirm(`Delete folder "${folder.name}"?${warn}`)) return;
    await deleteSharedFlowFolder(folder.id);
    showToast(`Folder "${folder.name}" deleted`);
  }

  // ── Export ───────────────────────────────────────────────────────────────────

  async function handleExportAll() {
    setExporting(true);
    try { await downloadSharedFlowsAsZip(functions, folders); }
    finally { setExporting(false); }
  }

  async function handleExportFolder(folder: SharedFlowFolder) {
    // Export all functions in this folder and its subfolders
    const allFolderIds = new Set<string>();
    function collectIds(id: string) {
      allFolderIds.add(id);
      folders.filter(f => f.parentId === id).forEach(f => collectIds(f.id));
    }
    collectIds(folder.id);
    const fns = functions.filter(f => f.folderId && allFolderIds.has(f.folderId));
    const relevantFolders = folders.filter(f => allFolderIds.has(f.id));
    if (fns.length === 0) { showToast("No functions in this folder"); return; }
    setExporting(true);
    try { await downloadSharedFlowsAsZip(fns, relevantFolders, `${folder.name}-functions.zip`); }
    finally { setExporting(false); }
  }

  // ── Import ───────────────────────────────────────────────────────────────────

  function handleImportClick() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,.zip,.mimiq-fn.json,application/json,application/zip";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setImporting(true);
      try {
        const result = await importSharedFlows(file);
        if (result.errors.length > 0) {
          alert(`Import completed with errors:\n${result.errors.join("\n")}\n\nImported: ${result.imported}, Skipped: ${result.skipped}`);
        } else {
          showToast(`✓ Imported ${result.imported} function${result.imported !== 1 ? "s" : ""}`);
        }
      } catch (err) {
        alert(`Import failed: ${String(err)}`);
      } finally {
        setImporting(false);
      }
    };
    input.click();
  }

  // ── Duplicate ────────────────────────────────────────────────────────────────

  async function handleDuplicate(fn: SharedFlow) {
    const copy = await duplicateSharedFlow(fn.id);
    setSelectedId(copy.id);
    showToast(`Duplicated as "${copy.name}"`);
  }

  // ── Rename ───────────────────────────────────────────────────────────────────

  async function handleRenameConfirm() {
    if (!renaming || !renameVal.trim()) { setRenaming(null); return; }
    if (renaming.kind === "fn") {
      const fn = functions.find(f => f.id === renaming.id);
      if (fn) await saveSharedFlow({ ...fn, name: renameVal.trim() }, fn.id);
    } else {
      const folder = folders.find(f => f.id === renaming.id);
      if (folder) await saveSharedFlowFolder({ name: renameVal.trim(), parentId: folder.parentId }, folder.id);
    }
    setRenaming(null);
    showToast("Renamed");
  }

  // ── Move function to folder ──────────────────────────────────────────────────

  async function handleMoveFn(fn: SharedFlow, folderId: string | undefined) {
    await saveSharedFlow({ ...fn, folderId }, fn.id);
    showToast(`Moved to ${folderId ? folders.find(f => f.id === folderId)?.name ?? "folder" : "root"}`);
  }

  // ── Filtered search ──────────────────────────────────────────────────────────

  const q = search.trim().toLowerCase();
  const filteredFns = q ? functions.filter(f =>
    f.name.toLowerCase().includes(q) || (f.description ?? "").toLowerCase().includes(q)
  ) : null; // null means "show tree"

  // ── Build folder tree ────────────────────────────────────────────────────────

  function renderFolderTree(parentId: string | undefined, depth: number): React.ReactNode {
    const childFolders   = folders.filter(f => (f.parentId ?? undefined) === parentId);
    const childFunctions = functions.filter(f => (f.folderId ?? undefined) === parentId);

    return (
      <>
        {childFolders.map(folder => (
          <div key={folder.id} className="fn-folder-node">
            <div className={`fn-folder-header${depth > 0 ? " fn-folder-header--nested" : ""}`}
              style={{ paddingLeft: `${0.5 + depth * 1}rem` }}>
              <button className="fn-folder-toggle" onClick={() => toggleFolder(folder.id)}>
                {expandedFolders.has(folder.id) ? "▾" : "▸"}
              </button>
              {renaming?.kind === "folder" && renaming.id === folder.id ? (
                <input className="fn-inline-rename" value={renameVal} autoFocus
                  onChange={e => setRenameVal(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") void handleRenameConfirm(); if (e.key === "Escape") setRenaming(null); }}
                  onBlur={() => void handleRenameConfirm()} />
              ) : (
                <span className="fn-folder-name" onDoubleClick={() => { setRenaming({ kind: "folder", id: folder.id }); setRenameVal(folder.name); }}>
                  📁 {folder.name}
                </span>
              )}
              <span className="fn-folder-count">{functions.filter(f => f.folderId === folder.id).length}</span>
              <div className="fn-folder-actions">
                <button className="fn-icon-btn" title="Add subfolder" onClick={() => { setCreatingFolder(true); setNewFolderName(""); setNewFolderParentId(folder.id); }}>+📁</button>
                <button className="fn-icon-btn" title={`Download all functions in "${folder.name}" as ZIP`} onClick={() => void handleExportFolder(folder)}>↓</button>
                <button className="fn-icon-btn fn-icon-btn--danger" title={`Delete folder "${folder.name}"`} onClick={() => void handleDeleteFolder(folder)}>✕</button>
              </div>
            </div>
            {expandedFolders.has(folder.id) && (
              <div className="fn-folder-children">
                {renderFolderTree(folder.id, depth + 1)}
              </div>
            )}
          </div>
        ))}
        {childFunctions.map(fn => renderFunctionItem(fn, depth))}
      </>
    );
  }

  function renderFunctionItem(fn: SharedFlow, depth: number): React.ReactNode {
    return (
      <div key={fn.id} className={`fn-list-item-wrap${selectedId === fn.id ? " fn-list-item-wrap--active" : ""}`}
        style={{ paddingLeft: `${0.5 + depth * 1}rem` }}>
        {renaming?.kind === "fn" && renaming.id === fn.id ? (
          <input className="fn-inline-rename" value={renameVal} autoFocus
            onChange={e => setRenameVal(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") void handleRenameConfirm(); if (e.key === "Escape") setRenaming(null); }}
            onBlur={() => void handleRenameConfirm()} />
        ) : (
          <button className="fn-list-item" onClick={() => setSelectedId(fn.id)}>
            <span className="fn-list-icon">♻</span>
            <span className="fn-list-name">{fn.name}</span>
            <span className="fn-list-meta">{fn.steps.length}s</span>
          </button>
        )}
        <div className="fn-list-item-actions">
          <button className="fn-icon-btn" title="Download this function as JSON" onClick={() => downloadSharedFlow(fn)}>↓</button>
          <button className="fn-icon-btn" title="Rename" onClick={() => { setRenaming({ kind: "fn", id: fn.id }); setRenameVal(fn.name); }}>✎</button>
          <button className="fn-icon-btn" title="Duplicate" onClick={() => void handleDuplicate(fn)}>⧉</button>
          <button className="fn-icon-btn fn-icon-btn--danger" title="Delete" onClick={() => void handleDeleteFn(fn)}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fn-tab">
      {toast && <div className="fn-toast">{toast}</div>}

      {/* ── Header ── */}
      <div className="fn-header">
        <div>
          <h2 className="fn-title">Function Library</h2>
          <p className="fn-subtitle">Reusable step sequences — call them from any test via ♻ Call Function.</p>
        </div>
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn btn--ghost" style={{ fontSize: "0.75rem" }}
            onClick={() => { setCreatingFolder(true); setNewFolderName(""); setNewFolderParentId(undefined); }}>
            + Folder
          </button>
          <button className="btn btn--ghost" style={{ fontSize: "0.75rem" }}
            onClick={handleImportClick} disabled={importing} title="Import functions from .json or .zip file">
            {importing ? "Importing…" : "↑ Import"}
          </button>
          {functions.length > 0 && (
            <button className="btn btn--ghost" style={{ fontSize: "0.75rem" }}
              onClick={() => void handleExportAll()} disabled={exporting}
              title="Export all functions and folders as a ZIP">
              {exporting ? "Exporting…" : `↓ Export All (${functions.length})`}
            </button>
          )}
          <button className="btn btn--primary fn-new-btn"
            onClick={() => { setCreating(true); setNewName(""); setNewDesc(""); setNewFolderId(undefined); }}>
            + New Function
          </button>
        </div>
      </div>

      {/* ── Create function dialog ── */}
      {creating && (
        <div className="fn-create-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setCreating(false); }}>
          <div className="fn-create-modal">
            <h3 className="fn-modal-title">New Function</h3>
            <label className="fn-modal-label">Name *</label>
            <input className="fn-modal-input" placeholder="e.g. Login as Admin" value={newName}
              onChange={e => setNewName(e.target.value)} autoFocus
              onKeyDown={e => { if (e.key === "Enter") void handleCreate(); if (e.key === "Escape") setCreating(false); }} />
            <label className="fn-modal-label">Description (optional)</label>
            <input className="fn-modal-input" placeholder="What this function does…" value={newDesc}
              onChange={e => setNewDesc(e.target.value)} />
            <label className="fn-modal-label">Folder (optional)</label>
            <select className="fn-modal-input" value={newFolderId ?? ""} onChange={e => setNewFolderId(e.target.value || undefined)}>
              <option value="">— Root (no folder) —</option>
              {folders.filter(f => !f.parentId).map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <div className="fn-modal-actions">
              <button className="btn btn--primary" onClick={() => void handleCreate()} disabled={!newName.trim()}>Create</button>
              <button className="btn btn--ghost" onClick={() => setCreating(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create folder dialog ── */}
      {creatingFolder && (
        <div className="fn-create-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setCreatingFolder(false); }}>
          <div className="fn-create-modal">
            <h3 className="fn-modal-title">New Folder</h3>
            <label className="fn-modal-label">Folder name *</label>
            <input className="fn-modal-input" placeholder="e.g. Authentication, Checkout" value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)} autoFocus
              onKeyDown={e => { if (e.key === "Enter") void handleCreateFolder(); if (e.key === "Escape") setCreatingFolder(false); }} />
            {newFolderParentId === undefined && folders.length > 0 && (
              <>
                <label className="fn-modal-label">Parent folder (optional)</label>
                <select className="fn-modal-input" value={newFolderParentId ?? ""} onChange={e => setNewFolderParentId(e.target.value || undefined)}>
                  <option value="">— Root —</option>
                  {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </>
            )}
            <div className="fn-modal-actions">
              <button className="btn btn--primary" onClick={() => void handleCreateFolder()} disabled={!newFolderName.trim()}>Create</button>
              <button className="btn btn--ghost" onClick={() => setCreatingFolder(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="fn-body">
        {/* ── Left: folder tree + function list ── */}
        <div className="fn-list-panel">
          <input className="fn-search" placeholder="🔍  Search functions…" value={search}
            onChange={e => setSearch(e.target.value)} />

          <div className="fn-tree">
            {filteredFns !== null ? (
              /* Search mode — flat list */
              filteredFns.length === 0
                ? <div className="fn-empty">No functions match "{search}"</div>
                : filteredFns.map(fn => renderFunctionItem(fn, 0))
            ) : (
              /* Tree mode */
              functions.length === 0 && folders.length === 0 ? (
                <div className="fn-empty">No functions yet. Click + New Function to start.</div>
              ) : (
                renderFolderTree(undefined, 0)
              )
            )}
          </div>
        </div>

        {/* ── Right: function detail ── */}
        <div className="fn-detail-panel">
          {!selectedFn ? (
            <div className="fn-detail-empty">Select a function to view details, or create a new one.</div>
          ) : (
            <FunctionDetail
              fn={selectedFn}
              folders={folders}
              onEditSteps={() => onEditFunction(selectedFn)}
              onDelete={() => void handleDeleteFn(selectedFn)}
              onDuplicate={() => void handleDuplicate(selectedFn)}
              onSaveMeta={updates => void saveSharedFlow({ ...selectedFn, ...updates }, selectedFn.id)}
              onMoveToFolder={folderId => void handleMoveFn(selectedFn, folderId)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── FunctionDetail ────────────────────────────────────────────────────────────

interface FunctionDetailProps {
  fn: SharedFlow;
  folders: SharedFlowFolder[];
  onEditSteps: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onSaveMeta: (updates: Partial<SharedFlow>) => void;
  onMoveToFolder: (folderId: string | undefined) => void;
}

function FunctionDetail({ fn, folders, onEditSteps, onDelete, onDuplicate, onSaveMeta, onMoveToFolder }: FunctionDetailProps) {
  const [params, setParams] = useState(fn.parameters);
  const [editingDesc, setEditingDesc] = useState(false);
  const [desc, setDesc] = useState(fn.description ?? "");
  const [addingParam, setAddingParam] = useState(false);
  const [newParam, setNewParam] = useState({ name: "", description: "", defaultValue: "" });

  // Reset when fn changes
  if (params !== fn.parameters) setParams(fn.parameters);

  function saveParams(updated: typeof params) {
    setParams(updated);
    onSaveMeta({ parameters: updated });
  }

  return (
    <div className="fn-detail">
      <div className="fn-detail-header">
        <div className="fn-detail-title-row">
          <h2 className="fn-detail-title">♻ {fn.name}</h2>
          <div className="fn-detail-actions">
            <button className="fn-action-btn" onClick={() => downloadSharedFlow(fn)} title="Download this function as a .json file">↓ Download</button>
            <button className="fn-action-btn" onClick={onDuplicate}>⧉ Duplicate</button>
            <button className="fn-action-btn fn-action-btn--danger" onClick={onDelete}>✕ Delete</button>
          </div>
        </div>

        {/* Folder placement */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <span style={{ fontSize: "0.72rem", color: "var(--color-text-muted)" }}>Folder:</span>
          <select style={{ fontSize: "0.75rem", padding: "1px 6px", background: "var(--color-bg)", color: "var(--color-text)", border: "1px solid var(--color-border)", borderRadius: 4 }}
            value={fn.folderId ?? ""}
            onChange={e => onMoveToFolder(e.target.value || undefined)}>
            <option value="">— Root —</option>
            {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>

        {/* Description */}
        {editingDesc ? (
          <div className="fn-desc-edit">
            <input className="fn-desc-input" placeholder="Describe what this function does…" value={desc} autoFocus
              onChange={e => setDesc(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { setEditingDesc(false); onSaveMeta({ description: desc.trim() || undefined }); } if (e.key === "Escape") setEditingDesc(false); }}
              onBlur={() => { setEditingDesc(false); onSaveMeta({ description: desc.trim() || undefined }); }} />
          </div>
        ) : (
          <p className="fn-desc" onClick={() => { setEditingDesc(true); setDesc(fn.description ?? ""); }}>
            {fn.description || <span className="fn-desc--empty">Click to add description…</span>}
          </p>
        )}

        <div className="fn-stats">
          <span className="fn-stat-chip">{fn.steps.length} steps</span>
          <span className="fn-stat-chip">{fn.parameters.length} parameters</span>
        </div>
      </div>


      {/* Parameters */}
      <section className="fn-section">
        <div className="fn-section-header">
          <h3 className="fn-section-title">Parameters</h3>
          <button className="fn-section-add-btn" onClick={() => setAddingParam(true)}>+ Add</button>
        </div>
        <p className="fn-section-hint">Inputs for callers. Inside steps use <code>${"{paramName}"}</code>.</p>
        {params.length === 0 && !addingParam && <p className="fn-empty-inline">No parameters — this function uses no inputs.</p>}
        {params.map((p, pi) => (
          <div key={pi} className="fn-param-row">
            <span className="fn-param-name"><code>${"{" + p.name + "}"}</code></span>
            {p.description && <span className="fn-param-desc">{p.description}</span>}
            {p.defaultValue && <span className="fn-param-default">default: "{p.defaultValue}"</span>}
            <button className="fn-param-remove" onClick={() => saveParams(params.filter((_, i) => i !== pi))}>✕</button>
          </div>
        ))}
        {addingParam && (
          <div className="fn-add-param-form">
            <input className="fn-param-input" placeholder="name *" value={newParam.name} autoFocus
              onChange={e => setNewParam(p => ({ ...p, name: e.target.value }))} />
            <input className="fn-param-input" placeholder="description" value={newParam.description}
              onChange={e => setNewParam(p => ({ ...p, description: e.target.value }))} />
            <input className="fn-param-input" placeholder='default value' value={newParam.defaultValue}
              onChange={e => setNewParam(p => ({ ...p, defaultValue: e.target.value }))} />
            <button className="btn btn--primary" style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem" }}
              disabled={!newParam.name.trim()}
              onClick={() => { saveParams([...params, { ...newParam, name: newParam.name.trim() }]); setNewParam({ name: "", description: "", defaultValue: "" }); setAddingParam(false); }}>
              Add
            </button>
            <button className="btn btn--ghost" style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem" }} onClick={() => setAddingParam(false)}>Cancel</button>
          </div>
        )}
      </section>

      {/* Steps — inline editor */}
      <section className="fn-section">
        <div className="fn-section-header">
          <h3 className="fn-section-title">Steps ({fn.steps.length})</h3>
        </div>
        <FunctionStepsEditor fn={fn} onSaveSteps={steps => onSaveMeta({ steps })} onEditInRecorder={onEditSteps} />
      </section>

      {/* Usage hint */}
      <section className="fn-section fn-section--usage">
        <h3 className="fn-section-title">How to call this function</h3>
        <p className="fn-usage-text">In the Command Toolbar → Flow category → <strong>♻ Call Function</strong></p>
        <div className="fn-usage-block">
          <code className="fn-usage-code" style={{ display: "block" }}>Call: {fn.name}</code>
          {fn.parameters.map(p => (
            <code key={p.name} className="fn-usage-code" style={{ display: "block", paddingLeft: "1rem" }}>
              {p.name}: {p.defaultValue ? `"${p.defaultValue}"` : `\${env:${p.name.toUpperCase()}}`}
            </code>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── FunctionStepsEditor — inline add/edit/reorder/delete for a function's flat step list ──

interface FunctionStepsEditorProps {
  fn: SharedFlow;
  onSaveSteps: (steps: IRStep[]) => void;
  onEditInRecorder: () => void;
}

function FunctionStepsEditor({ fn, onSaveSteps, onEditInRecorder }: FunctionStepsEditorProps) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const steps = fn.steps;

  function move(i: number, dir: "up" | "down") {
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= steps.length) return;
    const next = steps.slice();
    const tmp = next[i]!;
    next[i] = next[j]!;
    next[j] = tmp;
    onSaveSteps(next);
    if (editingIdx === i) setEditingIdx(j);
    else if (editingIdx === j) setEditingIdx(i);
  }

  function del(i: number) {
    if (editingIdx === i) setEditingIdx(null);
    onSaveSteps(steps.filter((_, si) => si !== i));
  }

  function update(i: number, updated: IRStep) {
    onSaveSteps(steps.map((s, si) => (si === i ? updated : s)));
  }

  function add(type: string) {
    onSaveSteps([...steps, makeDefaultNestedStep(type)]);
    setPickerOpen(false);
  }

  return (
    <div className="fn-steps-editor">
      {steps.length === 0 && (
        <p className="fn-empty-inline">No steps yet — add one below, or click ✎ Edit Steps in Recorder to record real taps on a device.</p>
      )}
      {steps.length > 0 && (
        <ol className="nested-step-list">
          {steps.map((step, i) => (
            <li key={step.id} className="nested-step-item">
              <div className="nested-step-row">
                <span className="nested-step-num">{i + 1}</span>
                <span className="nested-step-type">{step.type}</span>
                <span className="nested-step-desc">{stepDescription(step)}</span>
                <div className="nested-step-actions">
                  <button className="nsa-btn" title="Edit" onClick={() => setEditingIdx(editingIdx === i ? null : i)}>✎</button>
                  <button className="nsa-btn" title="Move up" disabled={i === 0} onClick={() => move(i, "up")}>↑</button>
                  <button className="nsa-btn" title="Move down" disabled={i === steps.length - 1} onClick={() => move(i, "down")}>↓</button>
                  <button className="nsa-btn nsa-btn--delete" title="Delete" onClick={() => del(i)}>✕</button>
                </div>
              </div>
              {editingIdx === i && (
                <div className="nested-step-edit">
                  {step.type === "tap" || step.type === "doubleTap" || step.type === "longPress" ? (
                    <p className="fn-empty-inline">
                      This step's locator can only be recorded from a live device — use ✎ Edit Steps in Recorder to change what it taps.
                    </p>
                  ) : (
                    <StepEditForm step={step} onSave={updated => { update(i, updated); setEditingIdx(null); }} onCancel={() => setEditingIdx(null)} />
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {pickerOpen ? (
        <div className="nested-picker">
          {NESTED_STEP_TYPES.map(({ type, label: lbl }) => (
            <button key={type} className="nested-picker-btn" onClick={() => add(type)}>{lbl}</button>
          ))}
          <button className="nested-picker-btn nested-picker-btn--cancel" onClick={() => setPickerOpen(false)}>Cancel</button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.4rem" }}>
          <button className="nested-add-btn" onClick={() => setPickerOpen(true)}>+ Add step</button>
          <button className="fn-edit-steps-btn" onClick={onEditInRecorder} title="Open this function's steps in the Recorder to record real taps">
            ✎ Edit Steps in Recorder
          </button>
        </div>
      )}
    </div>
  );
}
